import { useControlStore } from '@editorStores'
import useVisibilityFilterScope from './useVisibilityFilterScope'

interface ExtraVisibilityFilters {
  /** Number of the "Weiteres" overlays currently switched on (shown as the button badge). */
  activeCount: number
  /** Bulk show/hide-all, mirroring the IFC "Sonstiges" toggle: off unless every overlay is on. */
  toggleAll: () => void
}

/**
 * The leftover structural-planning visibility overlays (wind loads, supports, component labels) that
 * now live in the storey controls' "Weiteres" popover. Exposes the active count for the button badge
 * and a bulk toggle mirroring the IFC "Sonstiges" show/hide-all action. Only overlays whose switches
 * are shown on the current page are counted and toggled (see useVisibilityFilterScope).
 */
const useExtraVisibilityFilters = (): ExtraVisibilityFilters => {
  const visibility = useControlStore(state => state.structuralPlanningElementVisibility)
  const changeVisibility = useControlStore(state => state.changeStructuralPlanningElementVisibility)
  const showLabels = useControlStore(state => state.showLabels)
  const setShowLabels = useControlStore(state => state.setShowLabels)

  const { showWindLoads, showSupports } = useVisibilityFilterScope()

  const flags = [
    ...(showWindLoads ? [visibility.windLoadZones, visibility.horizontalWindLoads] : []),
    ...(showSupports ? [visibility.verticalTransmitters, visibility.connectors] : []),
    showLabels,
  ]
  const activeCount = flags.filter(Boolean).length

  const toggleAll = () => {
    const next = activeCount === 0
    if (showWindLoads) {
      changeVisibility('windLoadZones', next)
      changeVisibility('horizontalWindLoads', next)
    }
    if (showSupports) {
      changeVisibility('verticalTransmitters', next)
      changeVisibility('connectors', next)
    }
    setShowLabels(next)
  }

  return { activeCount, toggleAll }
}

export default useExtraVisibilityFilters
